/**
 * Today — the daily home.
 *
 * The screen renders from a single `GET /courses/:id/today` response, which is why all
 * three platforms agree about what "Today" means (docs/04-screen-specs.md B1).
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { RouteResponse } from '@vibelang/api-client';
import type { Pillar } from '@vibelang/core';
import {
  Banner,
  Button,
  EmptyState,
  ErrorState,
  FocusList,
  GoalRing,
  NextActionCard,
  PillarCard,
  Skeleton,
  StreakStrip,
  UsageMeter,
} from '@vibelang/ui';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { qk, retryIf } from '../lib/client.js';

/**
 * Where a learner practises each skill.
 *
 * The card used to link to `/learn/<pillar>`, which is a real route for three of the
 * five pillars and nothing at all for `speak` and `confidence`. Tapping your weakest
 * skill is the whole purpose of the card, and for two of them it did nothing — so the
 * mapping is explicit rather than derived from the name.
 */
const PILLAR_ROUTES: Readonly<Record<Pillar, string>> = {
  // Both are built by talking, not by reading about talking.
  speak: '/talk/scenarios',
  confidence: '/talk',
  words: '/learn/words',
  grammar: '/learn/grammar',
  listen: '/learn/listen',
};

type Today = RouteResponse<'getToday'>;

export interface TodayScreenProps {
  readonly courseId: string;
  readonly onNavigate: (route: string) => void;
  readonly onOpenUsage: () => void;
  readonly onOpenStreak: () => void;
  readonly offline?: boolean;
}

export function TodayScreen({
  courseId,
  onNavigate,
  onOpenUsage,
  onOpenStreak,
  offline = false,
}: TodayScreenProps): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery<Today>({
    queryKey: qk.today(courseId),
    queryFn: () => api.today(courseId) as Promise<Today>,
  });

  const loaded = query.data;

  // Emitted once the day has actually loaded, with the numbers the learner is looking
  // at. A view event fired on mount would count a skeleton as a visit and make the
  // retention numbers read better than they are.
  useEffect(() => {
    if (loaded === undefined) return;
    track('today_view', {
      goal_pct:
        loaded.goal.minutes === 0
          ? 0
          : Math.min(100, Math.round((loaded.goal.activeSeconds / 60 / loaded.goal.minutes) * 100)),
      due_count: loaded.dueReviews.count,
      streak: loaded.streak.currentDays,
      minutes_left:
        loaded.usage.voiceSecondsTotal === null
          ? null
          : Math.max(
              0,
              Math.round((loaded.usage.voiceSecondsTotal - loaded.usage.voiceSecondsUsed) / 60),
            ),
    });
  }, [loaded, track]);

  // The recommendation and its reason, so "did they take what we offered" is answerable.
  // `rank_reason` is the recommender's own explanation, not a guess made here.
  const nextAction = loaded?.nextAction ?? null;
  useEffect(() => {
    if (nextAction === null) return;
    track('today_next_up_shown', {
      type: nextAction.kind,
      id: nextAction.route,
      rank_reason: nextAction.reason,
    });
  }, [nextAction, track]);

  if (query.isPending) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={6} label="Loading your day" />
      </main>
    );
  }

  if (query.isError || query.data === undefined) {
    return (
      <main id="main" className="vl-page">
        <ErrorState
          title="We couldn't load your day"
          detail={errorDetail(query.error)}
          {...retryIf(query.error, () => void query.refetch())}
          // Review works from cache, so there is always something to do.
          alternative={{ label: 'Review from cache', onClick: () => onNavigate('/review/session') }}
        />
      </main>
    );
  }

  const today = query.data;
  const mean =
    today.skillScores.length === 0
      ? 0
      : today.skillScores.reduce((sum, s) => sum + s.score, 0) / today.skillScores.length;

  return (
    <main id="main" className="vl-page vl-today">
      {offline && (
        <Banner tone="offline">
          You&apos;re offline. Review and words still work; scores will sync later.
        </Banner>
      )}

      <header className="vl-today__header">
        <h1 className="vl-today__greeting">
          {today.greetingName === null ? 'Today' : `Hola, ${today.greetingName}`}
        </h1>
        <div className="vl-today__header-meta">
          <UsageMeter
            voiceSecondsUsed={today.usage.voiceSecondsUsed}
            voiceSecondsTotal={today.usage.voiceSecondsTotal}
            state={today.usage.state}
            resetsAt={today.usage.resetsAt}
            onOpen={onOpenUsage}
          />
        </div>
      </header>

      <section className="vl-today__goal" aria-label="Today's goal">
        <GoalRing
          minutes={today.goal.minutes}
          activeSeconds={today.goal.activeSeconds}
          met={today.goal.met}
        />
        <p className="vl-today__goal-text">
          {today.goal.met
            ? "Today's goal is done. Anything more is a bonus."
            : `${Math.max(0, today.goal.minutes - Math.floor(today.goal.activeSeconds / 60))} minutes to go.`}
        </p>
      </section>

      {today.nextAction === null ? (
        <EmptyState
          title="All caught up"
          body="Nothing is due. Want to learn five new words?"
          action={{
            label: 'Learn 5 words',
            onClick: () => {
              // Declining the recommendation is as informative as taking it: a
              // recommender nobody follows is worse than none, and only this tells us.
              track('today_next_up_rejected', {});
              onNavigate('/learn/words/deck');
            },
          }}
        />
      ) : (
        <NextActionCard
          title={today.nextAction.title}
          goal={today.nextAction.goal}
          reason={today.nextAction.reason}
          estimatedMinutes={today.nextAction.estimatedMinutes}
          requiresVoice={today.nextAction.requiresVoice}
          onStart={() => {
            track('today_next_up_tapped', {});
            onNavigate(today.nextAction?.route ?? '/learn');
          }}
        />
      )}

      {today.dueReviews.count > 0 && (
        <button
          type="button"
          className="vl-today__reviews"
          onClick={() => onNavigate('/review/session')}
        >
          <span>
            {today.dueReviews.count} {today.dueReviews.count === 1 ? 'card' : 'cards'} due
          </span>
          <span className="vl-today__reviews-time">
            about {today.dueReviews.estimatedMinutes} min
          </span>
        </button>
      )}

      {today.openMistakes.total > 0 && (
        <button
          type="button"
          className="vl-today__mistakes"
          onClick={() => onNavigate('/review/mistakes')}
        >
          {today.openMistakes.total} open {today.openMistakes.total === 1 ? 'mistake' : 'mistakes'}
          {today.openMistakes.blocking > 0 && ` · ${today.openMistakes.blocking} blocking`}
        </button>
      )}

      <StreakStrip
        strip={today.streak.strip}
        currentDays={today.streak.currentDays}
        currentWeeks={today.streak.currentWeeks}
        freezesLeft={today.streak.freezesLeft}
        onOpen={onOpenStreak}
      />

      <FocusList
        items={today.focus}
        onOpen={(route) => {
          // `FocusList` reports the route it was given, so the kind is recovered from
          // the item list rather than guessed from the URL.
          const item = today.focus.find((candidate) => candidate.route === route);
          track('today_focus_tapped', { focus_type: item?.kind ?? 'unknown' });
          onNavigate(route);
        }}
      />

      <section className="vl-today__pillars" aria-label="Your skills">
        {today.skillScores.map((skill) => (
          <PillarCard
            key={skill.pillar}
            pillar={skill.pillar}
            score={skill.score}
            delta7d={skill.delta7d}
            evaluatedAt={skill.evaluatedAt}
            // >15 points below the learner's own mean is the documented threshold.
            needsWork={mean - skill.score >= 15}
            onOpen={() => onNavigate(PILLAR_ROUTES[skill.pillar])}
          />
        ))}
      </section>

      {today.freshContent.count > 0 && (
        <button
          type="button"
          className="vl-today__fresh"
          onClick={() => onNavigate('/learn/listen')}
        >
          <span className="vl-today__fresh-eyebrow">New this week</span>
          <span>{today.freshContent.summary}</span>
        </button>
      )}
    </main>
  );
}

function errorDetail(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'problem' in error) {
    const problem = (error as { problem: { detail?: string; title: string } }).problem;
    return problem.detail ?? problem.title;
  }
  return undefined;
}

// ── B4 Usage detail sheet ───────────────────────────────────────────────────

/**
 * Honest accounting, with the upgrade offer *below* the information.
 *
 * The order is the design. A meter sheet that leads with an offer is a paywall wearing
 * a chart; one that leads with what counts and what does not is an explanation the
 * learner can act on. What does not count is the longer list, and saying so is the
 * point: review, words, grammar, listening and text chat are all unlimited, so hitting
 * the voice cap is never the end of a session.
 */
export function UsageSheet({
  used,
  total,
  resetsAt,
  state,
  trigger,
  onUpgrade,
  onNavigate,
}: {
  readonly used: number;
  /** Null means unlimited — shown as no meter at all rather than as a full bar. */
  readonly total: number | null;
  readonly resetsAt: number;
  readonly state: string;
  /** What brought the learner here, so the sheet's own conversion can be attributed. */
  readonly trigger: string;
  readonly onUpgrade: () => void;
  readonly onNavigate: (route: string) => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    track('usage_sheet_view', { used, limit: total, trigger });
  }, [used, total, trigger, track]);

  const unlimited = total === null;

  return (
    <section className="vl-sheet vl-usage" aria-labelledby="vl-usage-title">
      <h2 className="vl-sheet__title" id="vl-usage-title">
        {unlimited ? 'Unlimited speaking' : 'Your speaking time today'}
      </h2>

      {unlimited ? (
        <p className="vl-sheet__body">Nothing here is metered. There is no bar to watch.</p>
      ) : (
        <>
          <p className="vl-sheet__body">
            {Math.round(used / 60)} of {Math.round(total / 60)} minutes used. Resets{' '}
            {new Date(resetsAt).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
            .
          </p>
          <UsageMeter
            voiceSecondsUsed={used}
            voiceSecondsTotal={total}
            state={state as never}
            resetsAt={resetsAt}
            onOpen={() => undefined}
          />
        </>
      )}

      <dl className="vl-usage__counts">
        <dt>What counts</dt>
        <dd>Live scored speaking, and only that.</dd>
        <dt>What doesn&apos;t</dt>
        <dd>
          Review, words, grammar, listening and text conversation — all unlimited, all scored, on
          every plan.
        </dd>
      </dl>

      {/* Below the information, deliberately. */}
      {!unlimited && (
        <div className="vl-step__actions">
          <Button variant="secondary" onClick={() => onNavigate('/review')}>
            Keep practising for free
          </Button>
          <Button variant="ghost" onClick={onUpgrade}>
            See plans
          </Button>
        </div>
      )}
    </section>
  );
}

// ── B2 Streak detail ────────────────────────────────────────────────────────

/**
 * The streak, with a forgiving second axis and no guilt.
 *
 * Two deliberate departures from how streaks are usually built. The
 * **week-in-a-row** counter means a missed Tuesday does not erase a month, which is
 * the mechanic that makes a streak survivable. And a spent freeze says which day it
 * covered: a freeze that silently fills a gap is indistinguishable from a lie about
 * what the learner did.
 */
export function StreakDetailSheet({
  streak,
  onShare,
  onNavigate,
}: {
  readonly streak: {
    readonly currentDays: number;
    readonly bestDays: number;
    readonly currentWeeks: number;
    readonly bestWeeks: number;
    readonly freezesLeft: number;
    readonly strip: readonly { readonly localDate: string; readonly mark: string }[];
    readonly nextMilestone: { readonly target: number; readonly remaining: number } | null;
  };
  readonly onShare: (days: number) => void;
  readonly onNavigate: (route: string) => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    track('streak_detail_view', {});
  }, [track]);

  const frozen = streak.strip.filter((day) => day.mark === 'frozen');

  return (
    <section className="vl-sheet vl-streak" aria-labelledby="vl-streak-title">
      <h2 className="vl-sheet__title" id="vl-streak-title">
        {streak.currentDays === 0
          ? 'Your streak starts with one scored sentence'
          : `${streak.currentDays} ${streak.currentDays === 1 ? 'day' : 'days'} in a row`}
      </h2>

      {/* Nothing here is a zero. A learner with no streak is shown what starts one. */}
      {streak.currentDays === 0 ? (
        <p className="vl-sheet__body">
          Say one sentence and today counts. Review on its own keeps a streak alive too.
        </p>
      ) : (
        <p className="vl-sheet__body">
          Best: {streak.bestDays} days. Weeks in a row: {streak.currentWeeks} (best{' '}
          {streak.bestWeeks}).
        </p>
      )}

      <StreakStrip
        strip={streak.strip as never}
        currentDays={streak.currentDays}
        currentWeeks={streak.currentWeeks}
        freezesLeft={streak.freezesLeft}
      />

      {streak.nextMilestone !== null && (
        <p className="vl-note">
          {streak.nextMilestone.target} days — {streak.nextMilestone.remaining} to go.
        </p>
      )}

      <p className="vl-note">
        {streak.freezesLeft} {streak.freezesLeft === 1 ? 'freeze' : 'freezes'} left this month,
        applied automatically.
        {frozen.length > 0 && ` A freeze covered ${frozen.map((day) => day.localDate).join(', ')}.`}
      </p>

      <div className="vl-step__actions">
        {streak.currentDays > 0 && (
          <Button
            variant="secondary"
            onClick={() => {
              track('streak_share', { streak: streak.currentDays });
              onShare(streak.currentDays);
            }}
          >
            Share
          </Button>
        )}
        <Button onClick={() => onNavigate('/review/session')}>Keep it going</Button>
      </div>
    </section>
  );
}

// ── A12 Coachmark tour ──────────────────────────────────────────────────────

/** The five things worth pointing at, in the order a learner meets them. */
const TOUR_MARKS: readonly {
  readonly id: string;
  readonly title: string;
  readonly body: string;
}[] = [
  {
    id: 'goal',
    title: 'Your day, not your streak',
    body: 'This fills as you practise. Hitting it is the whole goal — anything past it is a bonus.',
  },
  {
    id: 'next_up',
    title: 'One next thing',
    body: 'We pick it and tell you why. If it is wrong, say so and we pick again.',
  },
  {
    id: 'mic',
    title: 'Hold, or tap — your choice',
    body: 'Tap-to-toggle is the default. Press-and-hold is in Settings if you prefer it.',
  },
  {
    id: 'review',
    title: 'Review is always free',
    body: 'It never uses speaking time, and it is still scored.',
  },
  {
    id: 'usage',
    title: 'What counts, and what does not',
    body: 'Only live speaking is metered. Everything else is unlimited on every plan.',
  },
];

/**
 * An overlay over the real Today screen, rather than a carousel of pictures of it.
 *
 * Pointing at the actual controls is the difference between a tour and an
 * advertisement; the learner finishes it already knowing where things are. `Skip` is
 * on every mark, because a tour you cannot leave is the first thing the app does to
 * you and it should not be coercive.
 */
export function CoachmarkTour({ onFinish }: { readonly onFinish: () => void }): JSX.Element | null {
  const { track } = useApp();
  const [index, setIndex] = useState(0);
  const mark = TOUR_MARKS[index];

  useEffect(() => {
    track('tour_start', {});
  }, [track]);

  useEffect(() => {
    track('tour_step', { index: index + 1 });
  }, [index, track]);

  if (mark === undefined) return null;

  const finish = () => {
    track('tour_complete', {});
    onFinish();
  };

  return (
    <div className="vl-tour" role="dialog" aria-labelledby="vl-tour-title" aria-modal="true">
      <div className="vl-tour__card" data-mark={mark.id}>
        <p className="vl-tour__progress">
          {index + 1} of {TOUR_MARKS.length}
        </p>
        <h2 className="vl-tour__title" id="vl-tour-title">
          {mark.title}
        </h2>
        <p className="vl-tour__body">{mark.body}</p>

        <div className="vl-step__actions">
          <Button onClick={() => (index + 1 < TOUR_MARKS.length ? setIndex(index + 1) : finish())}>
            {index + 1 < TOUR_MARKS.length ? 'Next' : 'Got it'}
          </Button>
          {/* On every mark, not only the first. */}
          <Button
            variant="ghost"
            onClick={() => {
              // Where they left is the useful part: a tour abandoned on mark one is a
              // different problem from one abandoned on mark four.
              track('tour_skip', { at_index: index + 1 });
              onFinish();
            }}
          >
            Skip tour
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── B3 Daily challenge ──────────────────────────────────────────────────────

/**
 * One three-minute set, the same for everyone learning that language today.
 *
 * "The same for everyone" is the mechanic — it makes the challenge a shared event
 * rather than another generated exercise — so the set comes from the server seeded on
 * the date and the language, and the client never filters it. Yesterday's set is shown
 * read-only with its answers, because a missed challenge is still worth reading and
 * hiding it just makes the streak feel punitive.
 */
export function DailyChallengeScreen({
  onNavigate,
}: {
  readonly onNavigate: (route: string) => void;
}): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  // The learner's own local date: midnight means their midnight, not UTC's.
  const localDate = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  const query = useQuery({
    queryKey: ['challenge', courseId, localDate] as const,
    queryFn: () => api.call('getDailyChallenge', { query: { courseId, localDate } }),
  });

  const [answers, setAnswers] = useState<Record<string, boolean>>({});

  const submit = useMutation({
    mutationFn: () =>
      api.call('submitDailyChallenge', {
        body: {
          courseId,
          localDate,
          answers: Object.entries(answers).map(([itemId, correct]) => ({ itemId, correct })),
        },
      }),
    onSuccess: (result) => {
      track('challenge_complete', { kind: 'recognition', correct: result.correct });
      void query.refetch();
    },
  });

  const challenge = query.data;
  useEffect(() => {
    if (challenge === undefined) return;
    track('challenge_view', { state: challenge.state });
  }, [challenge, track]);

  if (query.isPending) return <Skeleton lines={5} label="Loading today's challenge" />;
  if (query.error !== null || challenge === undefined) {
    return (
      <ErrorState
        title="We couldn't load today's challenge"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Review instead', onClick: () => onNavigate('/review/session') }}
      />
    );
  }

  if (challenge.state === 'unavailable') {
    return (
      <main id="main" className="vl-page">
        <h1>Daily challenge</h1>
        {/* The limit stated, with no paywall attached to it. */}
        <EmptyState
          title="No challenge for this language yet"
          body="Challenges need a content drop behind them. Your review queue and your words are unaffected."
          action={{ label: 'Review instead', onClick: () => onNavigate('/review/session') }}
        />
      </main>
    );
  }

  const done = challenge.state === 'complete' || challenge.state === 'expired';

  return (
    <main id="main" className="vl-page vl-challenge">
      <h1>Today&apos;s challenge</h1>
      <p className="vl-page__lede">
        {done
          ? 'Done for today. Everyone learning this language got the same set.'
          : `About ${Math.round(challenge.estimatedSeconds / 60)} minutes. Everyone learning this language today gets this set.`}
      </p>

      <ol className="vl-challenge__items">
        {challenge.items.map((item) => (
          <li key={item.id} className="vl-challenge__item">
            <p className="vl-challenge__prompt">{item.prompt}</p>

            {/* Answers appear only once the set is done, which is also the only state
                in which the server sends them. */}
            {item.answer !== null ? (
              <p className="vl-challenge__answer">{item.answer}</p>
            ) : (
              <div className="vl-challenge__grade">
                {([true, false] as const).map((correct) => (
                  <button
                    key={String(correct)}
                    type="button"
                    className="vl-button vl-button--secondary"
                    aria-pressed={answers[item.id] === correct}
                    onClick={() => {
                      if (Object.keys(answers).length === 0) {
                        track('challenge_start', { kind: 'recognition' });
                      }
                      setAnswers((current) => ({ ...current, [item.id]: correct }));
                    }}
                  >
                    {correct ? 'I knew it' : 'Not yet'}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>

      {!done && (
        <Button
          disabled={Object.keys(answers).length < challenge.items.length || submit.isPending}
          loading={submit.isPending}
          onClick={() => submit.mutate()}
        >
          Finish
        </Button>
      )}
    </main>
  );
}
